import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";

export const AuthErrorCode = {
	ACCOUNT_BLOCKED: "AUTH_ACCOUNT_BLOCKED",
	ACCOUNT_INACTIVE: "AUTH_ACCOUNT_INACTIVE",
	SESSION_REVOKED: "AUTH_SESSION_REVOKED",
	TOKEN_INVALID: "AUTH_TOKEN_INVALID",
	TOKEN_EXPIRED: "AUTH_TOKEN_EXPIRED",
	/** Permissions changed mid-session; refresh /me instead of logging out. */
	INSUFFICIENT_PERMISSION: "INSUFFICIENT_PERMISSION",
} as const;

export type AuthErrorCode = (typeof AuthErrorCode)[keyof typeof AuthErrorCode];

/** Survives hard redirect to /login after forceLogout. */
export const FORCE_LOGOUT_MESSAGE_KEY = "cms_portal_force_logout_message";

const FORCE_LOGOUT_CODES = new Set<string>([
	AuthErrorCode.ACCOUNT_BLOCKED,
	AuthErrorCode.ACCOUNT_INACTIVE,
	AuthErrorCode.SESSION_REVOKED,
	AuthErrorCode.TOKEN_INVALID,
]);

export interface ParsedApiError {
	message: string | null;
	code: string | null;
}

export interface SpreadsheetUploadValidationError {
	row: number | null;
	field: string;
	reason: string;
}

function readErrorData(
	error: FetchBaseQueryError | undefined,
): Record<string, unknown> | string | undefined {
	if (!error?.data) return undefined;
	return error.data as Record<string, unknown> | string;
}

export function extractApiErrorMessage(
	error: FetchBaseQueryError | undefined,
): string | null {
	const errorData = readErrorData(error);
	if (typeof errorData === "string" && errorData.trim()) {
		return errorData.trim();
	}
	if (errorData && typeof errorData === "object") {
		const message = errorData.message;
		if (typeof message === "string" && message.trim()) {
			return message.trim();
		}
		if (Array.isArray(message)) {
			const text = message
				.filter(
					(entry): entry is string =>
						typeof entry === "string" && entry.trim().length > 0,
				)
				.join(", ");
			if (text) return text;
		}
	}
	if (typeof error?.error === "string" && error.error.trim()) {
		return error.error.trim();
	}
	return null;
}

export function extractApiErrorCode(
	error: FetchBaseQueryError | undefined,
): string | null {
	const errorData = readErrorData(error);
	if (!errorData || typeof errorData !== "object") return null;
	const code = errorData.code;
	return typeof code === "string" && code.trim() ? code.trim() : null;
}

export function parseApiError(
	error: FetchBaseQueryError | undefined,
): ParsedApiError {
	return {
		message: extractApiErrorMessage(error),
		code: extractApiErrorCode(error),
	};
}

function asValidationRow(
	raw: unknown,
): SpreadsheetUploadValidationError | null {
	if (!raw || typeof raw !== "object") return null;
	const row = raw as Record<string, unknown>;
	const reason = typeof row.reason === "string" ? row.reason.trim() : "";
	if (!reason) return null;
	const field = typeof row.field === "string" ? row.field.trim() : "";
	const parsedRow = Number(row.row);
	const rowNumber = Number.isFinite(parsedRow) ? parsedRow : null;
	return { row: rowNumber, field, reason };
}

export function extractSpreadsheetUploadValidationErrors(
	error: FetchBaseQueryError | undefined,
): SpreadsheetUploadValidationError[] {
	const errorData = readErrorData(error);
	if (!errorData || typeof errorData !== "object") return [];
	const rawErrors = Array.isArray(errorData.errors) ? errorData.errors : [];
	return rawErrors
		.map(asValidationRow)
		.filter((item): item is SpreadsheetUploadValidationError => Boolean(item));
}

export function isSpreadsheetUploadValidationFailure(
	code: string | null,
	requestUrl: string,
	errors: SpreadsheetUploadValidationError[],
): boolean {
	if (errors.length === 0) return false;
	if (code && /UPLOAD_VALIDATION/i.test(code)) return true;
	return /\/upload(?:\/|$|\?)/i.test(requestUrl);
}

export function formatSpreadsheetUploadValidationToast(
	errors: SpreadsheetUploadValidationError[],
): {
	title: string;
	message: string;
	lines: Array<{ label?: string; text: string }>;
} {
	const count = errors.length;
	const title = `Validation Failed (${count} error${count === 1 ? "" : "s"}).`;
	const sorted = [...errors].sort((left, right) => {
		if (left.row == null && right.row == null) return 0;
		if (left.row == null) return 1;
		if (right.row == null) return -1;
		return left.row - right.row;
	});
	const lines = sorted.map((item) => ({
		label: item.row != null ? `Row ${item.row}:` : undefined,
		text: item.reason,
	}));
	const message = lines
		.map((line) => (line.label ? `${line.label} ${line.text}` : line.text))
		.join("\n");
	return { title, message, lines };
}

export function isForceLogoutAuthCode(code: string | null): boolean {
	return code !== null && FORCE_LOGOUT_CODES.has(code);
}

export function isInsufficientPermissionCode(code: string | null): boolean {
	return code === AuthErrorCode.INSUFFICIENT_PERMISSION;
}

export function isTokenExpiredAuthCode(code: string | null): boolean {
	return code === AuthErrorCode.TOKEN_EXPIRED;
}

export function isConflictError(error: unknown): boolean {
	if (!error || typeof error !== "object") return false;
	const status = "status" in error ? error.status : undefined;
	if (status === 409) return true;
	const data = "data" in error ? error.data : undefined;
	if (!data || typeof data !== "object") return false;
	const statusCode = "statusCode" in data ? data.statusCode : undefined;
	return statusCode === 409;
}

const NO_STATEMENT_DATA_UPLOADED_MESSAGE =
	"no statement data uploaded for this period.";

/** 404 from units GET when the period exists but nothing has been uploaded yet. */
export function isNoStatementDataUploadedError(error: unknown): boolean {
	if (!error || typeof error !== "object") return false;
	const status = "status" in error ? error.status : undefined;
	const data = "data" in error ? error.data : undefined;
	const statusCode =
		data && typeof data === "object" && "statusCode" in data
			? data.statusCode
			: undefined;
	if (status !== 404 && statusCode !== 404) return false;
	const message = extractApiErrorMessage(error as FetchBaseQueryError);
	return (
		(message ?? "").trim().toLowerCase() === NO_STATEMENT_DATA_UPLOADED_MESSAGE
	);
}
